import { ENDORSEMENT_KINDS, type EndorsementKind, type Millimes } from '@beta-assur/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { day, money, parseDinars, toDinarsInput } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';

// Garanties et avenants de la fiche contrat (BA-069). Montants saisis en dinars, envoyés en
// millimes ; un avenant de prime recalcule les échéances futures côté API.

interface Coverage {
  id: string;
  code: string;
  label: string;
  sumInsured: number | null;
  premiumPart: number | null;
}
interface Endorsement {
  id: string;
  kind: EndorsementKind;
  effectiveDate: string;
  deltaPremium: Millimes;
  note: string | null;
}

interface CoverageDraft {
  code: string;
  label: string;
  sumInsured: string;
  premiumPart: string;
}

const toDraft = (c: Coverage): CoverageDraft => ({
  code: c.code,
  label: c.label,
  sumInsured: c.sumInsured === null ? '' : toDinarsInput(c.sumInsured),
  premiumPart: c.premiumPart === null ? '' : toDinarsInput(c.premiumPart),
});

/** Saisie en dinars, éventuellement négative (baisse de prime). */
function parseSignedDinars(input: string): number | null {
  const trimmed = input.trim();
  const negative = trimmed.startsWith('-');
  const value = parseDinars(negative ? trimmed.slice(1) : trimmed);
  return value === null ? null : negative ? -value : value;
}

export function CoveragesCard({ contractId, editable }: { contractId: string; editable: boolean }) {
  const { t, language } = useI18n();
  const path = `/v1/contracts/${contractId}/coverages`;
  const query = useApiQuery<{ items: Coverage[] }>(path);
  const [drafts, setDrafts] = useState<CoverageDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useApiMutation<{ items: unknown[] }>('PUT', path, {
    invalidate: [path],
    onSuccess: () => setDrafts(null),
  });

  function submit() {
    if (!drafts) return;
    const items = [];
    for (const d of drafts) {
      const sumInsured = d.sumInsured.trim() ? parseDinars(d.sumInsured) : null;
      const premiumPart = d.premiumPart.trim() ? parseDinars(d.premiumPart) : null;
      if (
        !d.code.trim() ||
        !d.label.trim() ||
        (d.sumInsured.trim() && sumInsured === null) ||
        (d.premiumPart.trim() && premiumPart === null)
      )
        return setError(t.coverage.invalid);
      items.push({ code: d.code.trim(), label: d.label.trim(), sumInsured, premiumPart });
    }
    setError(null);
    save.mutate({ items });
  }

  const update = (index: number, patch: Partial<CoverageDraft>) =>
    setDrafts((list) => (list ?? []).map((d, i) => (i === index ? { ...d, ...patch } : d)));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.coverage.title}</CardTitle>
        {editable && drafts === null && query.data ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDrafts(query.data.items.map(toDraft))}
          >
            {t.common.edit}
          </Button>
        ) : null}
      </CardHeader>
      {drafts ? (
        <CardContent className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">{t.coverage.noHealthData}</p>
          {drafts.map((d, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[8rem_1fr_9rem_9rem_auto]">
              <Input
                aria-label={`${t.coverage.code} ${i + 1}`}
                value={d.code}
                maxLength={30}
                onChange={(e) => update(i, { code: e.target.value })}
              />
              <Input
                aria-label={`${t.coverage.label} ${i + 1}`}
                value={d.label}
                maxLength={120}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <Input
                aria-label={`${t.coverage.sumInsured} ${i + 1}`}
                inputMode="decimal"
                value={d.sumInsured}
                onChange={(e) => update(i, { sumInsured: e.target.value })}
              />
              <Input
                aria-label={`${t.coverage.premiumPart} ${i + 1}`}
                inputMode="decimal"
                value={d.premiumPart}
                onChange={(e) => update(i, { premiumPart: e.target.value })}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDrafts((list) => (list ?? []).filter((_, j) => j !== i))}
              >
                {t.common.delete}
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setDrafts((list) => [
                  ...(list ?? []),
                  { code: '', label: '', sumInsured: '', premiumPart: '' },
                ])
              }
            >
              {t.coverage.add}
            </Button>
            <Button size="sm" disabled={save.isPending} onClick={submit}>
              {t.common.save}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDrafts(null)}>
              {t.common.cancel}
            </Button>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <ActionError error={save.error} />
        </CardContent>
      ) : (
        <QueryView query={query}>
          {(data) =>
            data.items.length === 0 ? (
              <Empty>{t.coverage.none}</Empty>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>{t.coverage.code}</TH>
                    <TH>{t.coverage.label}</TH>
                    <TH className="text-end">{t.coverage.sumInsured}</TH>
                    <TH className="text-end">{t.coverage.premiumPart}</TH>
                  </tr>
                </THead>
                <TBody>
                  {data.items.map((c) => (
                    <TR key={c.id}>
                      <TD>{c.code}</TD>
                      <TD>{c.label}</TD>
                      <TD className="text-end tabular-nums">
                        {c.sumInsured === null ? '-' : money(c.sumInsured, language)}
                      </TD>
                      <TD className="text-end tabular-nums">
                        {c.premiumPart === null ? '-' : money(c.premiumPart, language)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryView>
      )}
    </Card>
  );
}

export function EndorsementsCard({
  contractId,
  editable,
}: {
  contractId: string;
  editable: boolean;
}) {
  const { t, language } = useI18n();
  const path = `/v1/contracts/${contractId}/endorsements`;
  const query = useApiQuery<{ items: Endorsement[]; currentPremium: Millimes }>(path);
  const [form, setForm] = useState({
    kind: 'premium_change' as EndorsementKind,
    effectiveDate: '',
    delta: '',
    note: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const create = useApiMutation<Record<string, unknown>>('POST', path, {
    invalidate: [path, `/v1/contracts/${contractId}`],
    onSuccess: () => {
      setSaved(true);
      setForm((f) => ({ ...f, delta: '', note: '' }));
    },
  });
  useEffect(() => setSaved(false), [form.effectiveDate, form.kind]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const deltaPremium = form.delta.trim() ? parseSignedDinars(form.delta) : 0;
    if (!form.effectiveDate || deltaPremium === null) return setError(t.coverage.invalid);
    setError(null);
    create.mutate({
      kind: form.kind,
      effectiveDate: form.effectiveDate,
      deltaPremium,
      ...(form.note.trim() ? { note: form.note.trim() } : {}),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.endorsements.title}</CardTitle>
        {query.data ? (
          <span className="text-sm text-muted-foreground">
            {t.endorsements.currentPremium} : {money(query.data.currentPremium, language)}
          </span>
        ) : null}
      </CardHeader>
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty>{t.endorsements.none}</Empty>
          ) : (
            <Table>
              <TBody>
                {data.items.map((e) => (
                  <TR key={e.id}>
                    <TD className="whitespace-nowrap">{day(e.effectiveDate, language)}</TD>
                    <TD>{t.endorsements.kinds[e.kind]}</TD>
                    <TD className="text-end tabular-nums">
                      {e.deltaPremium > 0 ? '+' : e.deltaPremium < 0 ? '-' : ''}
                      {money(Math.abs(e.deltaPremium), language)}
                    </TD>
                    <TD className="text-muted-foreground">{e.note ?? ''}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
      {editable ? (
        <CardContent className="border-t border-border">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t.endorsements.kind}>
              <Select
                value={form.kind}
                onChange={(e) =>
                  setForm((f) => ({ ...f, kind: e.target.value as EndorsementKind }))
                }
              >
                {ENDORSEMENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t.endorsements.kinds[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t.endorsements.effectiveDate}>
              <Input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
              />
            </Field>
            <Field label={t.endorsements.delta} hint={t.endorsements.deltaHint}>
              <Input
                inputMode="decimal"
                value={form.delta}
                onChange={(e) => setForm((f) => ({ ...f, delta: e.target.value }))}
              />
            </Field>
            <Field label={t.endorsements.note}>
              <Input
                value={form.note}
                maxLength={500}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              />
            </Field>
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-4">
              <Button type="submit" disabled={create.isPending}>
                {t.endorsements.add}
              </Button>
              {saved ? (
                <span role="status" className="text-sm text-success">
                  {t.endorsements.saved}
                </span>
              ) : null}
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <ActionError error={create.error} />
            </div>
          </form>
        </CardContent>
      ) : null}
    </Card>
  );
}
